import { AppBar, Box, Divider, Stack, Toolbar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation } from 'react-router';
import SuspensionBanner from '../../../components/alert/SuspensionBanner';
import { IconButtonAnimate } from '../../../components/animate';
import Iconify from '../../../components/Iconify';
import ReachLinkLogo from '../../../components/Logo';
import Logo from '../../../components/OrganizationMemberLogo';
import { HEADER, NAVBAR } from '../../../config';
import useAuth from '../../../hooks/useAuth';
import useLocales from '../../../hooks/useLocales';
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
import { PATH_MAIN } from '../../../routes/paths';
import AccountPopover from './AccountPopover';
import LanguageDropdown from './LanguageDropdown';
import NotificationsPopover from './NotificationsPopover';
import ToolsAccordion from './ToolsAccordion';

type RootStyleProps = {
    isCollapse: boolean;
    isOffset: boolean;
    verticalLayout: boolean;
};

const RootStyle = styled(AppBar, {
    shouldForwardProp: (prop) =>
        prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})<RootStyleProps>(({ isCollapse, isOffset, verticalLayout, theme }) => ({
    // backdrop-filter removed for now: its repaint (triggered by header menu
    // scroll-lock overflow toggling) flickered the sidebar. Use a solid bg.
    backgroundColor: theme.palette.background.default,
    boxShadow: 'none',
    height: HEADER.MOBILE_HEIGHT,
    zIndex: theme.zIndex.appBar + 1,
    transition: theme.transitions.create(['width', 'height'], {
        duration: theme.transitions.duration.shorter,
    }),
    [theme.breakpoints.up('lg')]: {
        height: HEADER.MAIN_DESKTOP_HEIGHT,
        width: `calc(100% - ${NAVBAR.MAIN_WIDTH + 1}px)`,
        ...(isCollapse && {
            width: `calc(100% - ${NAVBAR.MAIN_COLLAPSE_WIDTH}px)`,
        }),
        ...(isOffset && {
            height: HEADER.MAIN_DESKTOP_OFFSET_HEIGHT,
        }),
        ...(verticalLayout && {
            width: '100%',
            height: HEADER.MAIN_DESKTOP_OFFSET_HEIGHT,
            backgroundColor: theme.palette.background.default,
        }),
    },
}));

type Props = {
    onOpenSidebar: VoidFunction;
    isCollapse?: boolean;
    verticalLayout?: boolean;
};

export default function MainHeader({
    onOpenSidebar,
    isCollapse = false,
    verticalLayout = false,
}: Props) {
    const { isAdmin, isProvider, user } = useAuth();
    const { translate } = useLocales();
    const isOffset = useOffSetTop(HEADER.MAIN_DESKTOP_HEIGHT) && !verticalLayout;
    const isDesktop = useResponsive('up', 'lg');

    return (
        <RootStyle isCollapse={isCollapse} isOffset={isOffset} verticalLayout={verticalLayout}>
            <Toolbar
                sx={{
                    minHeight: '100% !important',
                    px: { lg: 5 },
                }}
            >
                {isDesktop && verticalLayout && (
                    <ReachLinkLogo
                        outerSx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mr: 2.5,
                            width: '170px',
                        }}
                    />
                )}

                {!isDesktop && (
                    <IconButtonAnimate
                        onClick={onOpenSidebar}
                        aria-label={translate('layouts.headerNav.openMenuAria')}
                        sx={{ mr: 1, color: 'text.primary' }}
                    >
                        <Iconify aria-hidden icon="eva:menu-2-fill" />
                    </IconButtonAnimate>
                )}

                <Box sx={{ flexGrow: 1 }} />

                <SuspensionBanner
                    sx={{
                        flexGrow: 2,
                        mx: 2,
                    }}
                />

                <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
                    {(isAdmin || isProvider) && <ToolsAccordion />}
                    {/* NotificationsPopover temporarily disabled*/}
                    <NotificationsPopover />
                    <LanguageDropdown />
                    <Divider orientation="vertical" flexItem />
                    <Logo entity={user?.organizationProfile?.organization} />
                    <Logo entity={user?.payerProfile?.payer} />
                    <AccountPopover />
                </Stack>
            </Toolbar>
        </RootStyle>
    );
}
